<script>
export default {
    props: {
        isOpen: {
            type: Boolean,
            default: false
        },
        minWidth: {
            type: Number,
            default: 400 // px
        },
        maxWidth: {
            type: Number,
            default: 650 // px
        },
        initialWidth: {
            type: Number,
            default: 575 // px
        }
    },

    data() {
        return {
            isDragging: false,
            initialX: 0,
            sidebarWidth: this.initialWidth
        };
    },

    emits: ['closeSidebar'],

    mounted() {
        // Load persistent width from localStorage
        this.loadPersistentWidth();

        // Set initial CSS variable
        this.updateCssVariable();
        
        // Add event listeners for dragging
        window.addEventListener('mousemove', this.handleMouseMove);
        window.addEventListener('mouseup', this.handleMouseUp);
    },

    beforeUnmount() {
        // Clean up event listeners
        window.removeEventListener('mousemove', this.handleMouseMove);
        window.removeEventListener('mouseup', this.handleMouseUp);
    },

    computed: {
        // Determines the current size state of the sidebar (min, middle, or max)
        sidebarSizeState() {
            const tolerance = 5; // Small tolerance for float comparison
            
            if (Math.abs(this.sidebarWidth - this.minWidth) <= tolerance) {
                return 'min'; // Sidebar is at minimum width
            } else if (Math.abs(this.sidebarWidth - this.maxWidth) <= tolerance) {
                return 'max'; // Sidebar is at maximum width
            } else {
                return 'middle'; // Sidebar is between min and max
            }
        },
        
        // Controls visibility of the expand button (hidden when at max width)
        canExpand() {
            return this.sidebarSizeState !== 'max';
        },
        
        // Controls visibility of the contract button (hidden when at min width)
        canContract() {
            return this.sidebarSizeState !== 'min';
        }
    },

    methods: {
        closeSidebar() {
            this.$emit('closeSidebar');
        },
        startDrag(e) {
            this.isDragging = true;
            this.initialX = e.clientX;
            e.preventDefault();
        },
        handleMouseMove(e) {
            if (!this.isDragging) return;
            
            // Calculate new width based on mouse position (drag from left edge)
            const deltaX = this.initialX - e.clientX;

            this.sidebarWidth = Math.max(
                this.minWidth, 
                Math.min(this.maxWidth, this.sidebarWidth + deltaX)
            );
            this.updateCssVariable();
            this.initialX = e.clientX;
        },
        handleMouseUp() {
            if (this.isDragging) {
                this.persistWidth();
            }
            this.isDragging = false;
        },
        updateCssVariable() {
            // Update CSS variable for sidebar width
            document.documentElement.style.setProperty('--sidebar-width', `${this.sidebarWidth}px`);
            
            // Update margin if sidebar is open
            if (this.isOpen) {
                document.documentElement.style.setProperty('--sidebar-space', 'calc(var(--sidebar-width) + var(--n-space-l))');
            }
        },
        persistWidth() {
            try {
                localStorage.setItem('sidebar-width', this.sidebarWidth);
            } catch (e) {
                console.warn('Could not save sidebar width to localStorage', e);
            }
        },
        loadPersistentWidth() {
            try {
                const savedWidth = localStorage.getItem('sidebar-width');
                if (savedWidth) {
                    const width = parseInt(savedWidth, 10);
                    this.sidebarWidth = Math.max(
                        this.minWidth, 
                        Math.min(this.maxWidth, width)
                    );
                }
            } catch (e) {
                console.warn('Could not load sidebar width from localStorage', e);
            }
        },
        // Increases sidebar width by one step (min→middle→max)
        expandSidebar() {
            if (this.sidebarSizeState === 'min') {
                // If at minimum width, expand to middle size
                this.sidebarWidth = this.initialWidth;
            } else if (this.sidebarSizeState === 'middle') {
                // If at middle width, expand to maximum size
                this.sidebarWidth = this.maxWidth;
            }
            
            // Update CSS variables and save to localStorage
            this.updateCssVariable();
            this.persistWidth();
        },
        
        // Decreases sidebar width by one step (max→middle→min)
        contractSidebar() {
            if (this.sidebarSizeState === 'max') {
                // If at maximum width, contract to middle size
                this.sidebarWidth = this.initialWidth;
            } else if (this.sidebarSizeState === 'middle') {
                // If at middle width, contract to minimum size
                this.sidebarWidth = this.minWidth;
            }
            
            // Update CSS variables and save to localStorage
            this.updateCssVariable();
            this.persistWidth();
        }
    },

    watch: {
        isOpen(newValue) {
            // Update body margin based on sidebar state
            document.documentElement.style.setProperty('--sidebar-space', 
                newValue ? 'calc(var(--sidebar-width) + var(--n-space-l))' : 'var(--n-space-l)');
        }
    },
}
</script>

<template>
    <div :class="[
            'up-sidebar',
            {'up-sidebar--with-footer': $slots.footer},
            {'up-sidebar--open': isOpen},
        ]">
        <!-- Resize handle -->
        <div 
            :class="['resize-handle', {'resize-handle--active': isDragging}]" 
            @mousedown="startDrag"
        />

        <!-- Resize buttons (for smaller screens) -->
        <nord-button v-if="isOpen" :disabled="!canExpand" square class="up-sidebar-exapnd-toggle" @click="expandSidebar" size="s">
            <nord-icon name="arrow-left" size="s" class="up-sidebar-toggle--icon"></nord-icon>
        </nord-button>
        <nord-button v-if="isOpen" :disabled="!canContract" square class="up-sidebar-contract-toggle" @click="contractSidebar" size="s">
            <nord-icon name="arrow-right" size="s" class="up-sidebar-toggle--icon"></nord-icon>
        </nord-button>
        
        <!-- Close sidebar button -->
        <nord-button v-if="isOpen" square class="up-sidebar-toggle" @click="closeSidebar" size="s">
            <nord-icon name="arrow-right-double" size="s" class="up-sidebar-toggle--icon"></nord-icon>
        </nord-button>

        <!-- Sidebar content -->
        <slot></slot>
    </div>
</template>

<style lang="scss" scoped>
.up-sidebar {
    position: fixed;
    top: 72px;
    bottom: 0;
    right: calc(-1 * var(--sidebar-width));
    width: var(--sidebar-width);
    background-color: white;
    z-index: 10;
    transition: right 0.2s;
    max-width: calc(100% - 20px);
    box-shadow: var(--n-box-shadow-card);

    &--open {
        right: 0;
    }

    &--with-footer {
        padding-bottom: 70px;
        
        .up-sidebar-footer {
            position: absolute;
            bottom: 0;
            left: 0;
            right: 0;
        }
    }

    .up-sidebar-toggle {
        position: absolute;
        left: -14px;
        top: 130px;
        z-index: 150;
        opacity: 1;
        --n-button-border-radius: var(--n-border-radius-circle);

        &:hover {
            --n-button-background-color: var(--n-color-accent);
            --n-button-color: var(--n-color-text-on-accent);

            .up-sidebar-toggle--icon {
                color: var(--n-color-text-on-accent);
            }
        }
    }

    .up-sidebar-exapnd-toggle,
    .up-sidebar-contract-toggle {
        display: none;
        position: absolute;
        left: -14px;
        top: 130px;
        z-index: 150;
        opacity: 0;
        --n-button-border-radius: var(--n-border-radius-circle);
    }

    .up-sidebar-exapnd-toggle {
        top: 210px;
    }

    .up-sidebar-contract-toggle {
        top: 250px;
    }
    
    .resize-handle {
        position: absolute;
        top: 0;
        left: 0;
        width: 8px;
        height: 100%;
        cursor: ew-resize;
        z-index: 11;

        &:after {
            content: '';
            display: block;
            position: absolute;
            top: 0;
            left: 0;
            width: 2px;
            height: 100%;
            background-color: transparent;
            transition: background-color 0.1s linear 0.1s;
        }
        
        &:hover,
        &--active {
            &:after {
                background-color: var(--n-color-accent);
            }
        }
    }

    @media screen and (max-width: 1024px) {
        .up-sidebar-exapnd-toggle,
        .up-sidebar-contract-toggle {
            display: block;
            opacity: 1;
        }
        
    }

    @media screen and (max-width: 480px) {
        width: calc(100% - 12px);
        max-width: calc(100% - 12px);
        
        &:not(.up-sidebar--open) {
            right: -100%;
        }

        .up-sidebar-toggle {
            left: -10px;
        }

        .up-sidebar-exapnd-toggle,
        .up-sidebar-contract-toggle {
            display: none;
        }
    }
}
</style>